<template>
  <div>
    <!-- live support status customers see on the Support button: automatic hours, or switched on / off by hand -->
    <div class="support-bar mx-2 mb-2">
      <span class="support-bar__label">Support status</span>
      <div class="support-bar__modes" role="group" aria-label="Support status">
        <button v-for="m in SUPPORT_MODES" :key="m.key" type="button" :class="{ 'is-on': supportForm.mode === m.key, ['is-' + m.key]: true }" :disabled="supportSaving" @click="setSupport({ mode: m.key })">{{ m.label }}</button>
      </div>
      <template v-if="supportForm.mode === 'auto'">
        <label class="support-bar__hours">Online from
          <select :value="supportForm.from" :disabled="supportSaving" @change="setSupport({ from: Number($event.target.value) })">
            <option v-for="h in 24" :key="'f' + h" :value="h - 1">{{ hourLabel(h - 1) }}</option>
          </select>
          to
          <select :value="supportForm.to" :disabled="supportSaving" @change="setSupport({ to: Number($event.target.value) })">
            <option v-for="h in 24" :key="'t' + h" :value="h">{{ hourLabel(h) }}</option>
          </select>
          Sydney time
        </label>
      </template>
      <span v-else class="support-bar__note">Stays {{ supportForm.mode }} until you switch back to Automatic</span>
      <span class="support-bar__now" :class="supportState.online ? 'is-online' : 'is-offline'">
        <i></i>Customers see: {{ supportState.online ? 'Online now' : 'Offline' }}
      </span>
    </div>
    <!-- push notifications for this device (installed app on a phone) -->
    <div class="flex items-center gap-2 mx-2 mb-2 text-sm" v-if="pushStatus !== 'unsupported'">
      <v-btn size="small" :class="pushStatus === 'on' ? 'bg-success text-light' : 'bg-dark text-light'" :prepend-icon="pushStatus === 'on' ? 'mdi-bell-ring' : 'mdi-bell-outline'"
        :disabled="pushBusy || pushStatus === 'denied'" @click="togglePush">{{ pushStatus === 'on' ? 'Notifications on' : 'Enable notifications on this device' }}</v-btn>
      <v-btn v-if="pushStatus === 'on'" size="small" variant="text" :disabled="pushBusy" @click="testPush">Send test</v-btn>
      <span v-if="pushStatus === 'denied'" class="text-red-500">Notifications are blocked for this site in the browser settings.</span>
      <!-- the live chat as its own phone app: open on the phone, then Chrome menu → Install app -->
      <a href="/admin/livechat" target="_blank" rel="noopener" class="ml-auto underline" title="Open the chat app on your phone in Chrome, then use the menu → Install app">Phone chat app</a>
    </div>
  </div>
  <div class="admin-chat-container">
    <UserList :users="sortedCustomers" @selectUser="selectUser" @unRead="unRead" :class="{ 'hidden md:block': mobileView === 'chat' }" />
    <div ref="chatColumn" class=" w-full flex flex-col justify-start items-center" :class="{ 'hidden md:flex': mobileView === 'list' }" :style="`height: ${computedHeight}px;`">
      <!-- selected customer: quick jump to their licences (filtered by email) -->
      <div v-if="selectedUser" class="w-full flex items-center justify-between px-3 py-1 text-sm border-bottom mb-2">
        <!-- phone: back to the customer list -->
        <v-btn icon="mdi-arrow-left" size="small" variant="text" class="md:hidden mr-1" @click="mobileView = 'list'"></v-btn>
        <div class="flex flex-col">
          <span><b>{{ selectedUser.name }}</b> <span class="text-gray-500">{{ selectedUser.email }}</span></span>
          <!-- account age, licences since sign-up, money added -->
          <span v-if="summary" class="text-xs text-gray-500 flex flex-wrap gap-x-3">
            <span title="Account created">Account {{ accountAge }} (since {{ fmtDate(summary.created_at) }})</span>
            <span>{{ summary.licenses_total }} licence{{ summary.licenses_total === 1 ? '' : 's' }} ({{ summary.licenses_active }} active)</span>
            <span>{{ summary.purchases }} purchase{{ summary.purchases === 1 ? '' : 's' }} / {{ summary.extensions }} extension{{ summary.extensions === 1 ? '' : 's' }}</span>
            <span>${{ money(summary.topup_total) }} added in {{ summary.topups }} top-up{{ summary.topups === 1 ? '' : 's' }}</span>
            <span>balance ${{ money(summary.balance) }}</span>
            <span v-if="summary.ipaddress" title="IP recorded at sign-up and where it is located">
              Reg. IP {{ summary.ipaddress }}
              <template v-if="summary.ip_geo"> · {{ summary.ip_geo.country }}<span v-if="summary.ip_geo.city"> ({{ summary.ip_geo.city }})</span><span v-if="summary.ip_geo.proxy || summary.ip_geo.hosting" class="text-red-500"> · VPN/hosting IP</span></template>
              <span v-if="summary.country && summary.ip_geo && summary.ip_geo.country !== summary.country" class="text-amber-500"> · profile says {{ summary.country }}</span>
              <span v-else-if="summary.country && !summary.ip_geo"> · profile: {{ summary.country }}</span>
            </span>
            <span v-else-if="summary.country">Country (profile): {{ summary.country }}</span>
          </span>
          <span v-else-if="summaryLoading" class="text-xs text-gray-500">Loading account summary…</span>
        </div>
        <div class="flex gap-2">
          <v-btn size="small" class="bg-dark text-light" prepend-icon="mdi-history" title="This customer's entries in the Audit log" @click="openActivity">Activity</v-btn>
          <v-btn size="small" class="bg-dark text-light" prepend-icon="mdi-key-variant" title="Open the Licences page filtered to this customer" @click="openLicenses">Licenses</v-btn>
        </div>
      </div>
      <!-- other accounts that signed up from the same IP -->
      <div v-if="selectedUser && linked && linked.accounts.length" class="linked-bar w-full" :class="{ 'is-open': linkedOpen }">
        <button type="button" class="linked-bar__top" :aria-expanded="linkedOpen" @click="linkedOpen = !linkedOpen">
          <v-icon icon="mdi-alert-outline" size="18"></v-icon>
          <b>Signed up from the same IP as {{ linked.accounts.length }} other account{{ linked.accounts.length === 1 ? '' : 's' }}</b>
          <v-icon class="linked-bar__chev" icon="mdi-chevron-down" size="18"></v-icon>
        </button>
        <div v-if="linkedOpen" class="linked-bar__body">
          <div class="linked-bar__ip">
            Sign-up IP <code>{{ linked.ip }}</code>
            <template v-if="linked.ip_geo"> · {{ linked.ip_geo.country }}<template v-if="linked.ip_geo.city"> ({{ linked.ip_geo.city }})</template> · {{ linked.ip_geo.proxy || linked.ip_geo.hosting ? 'VPN / hosting IP' : 'not a VPN or hosting IP' }}</template>
            · {{ linked.our_proxy_ip ? 'one of our proxies' : 'not one of our proxies' }}
          </div>
          <div v-for="a in linked.accounts" :key="a.user_id" class="linked-row">
            <div class="linked-row__who"><b>{{ a.name || '-' }}</b><span>{{ a.email }}</span></div>
            <div><small>Signed up</small>{{ fmtDate(a.created_at) }}</div>
            <div><small>Licences</small><span class="linked-tag" :class="a.licenses_active ? 'is-active' : 'is-none'">{{ a.licenses_active ? a.licenses_active + ' active' : 'none active' }}</span> of {{ a.licenses_total }}</div>
            <div><small>Top-ups</small>${{ money(a.topup_total) }} · {{ a.topups }}</div>
            <div class="linked-row__btns">
              <v-btn size="x-small" class="bg-dark text-light" @click="openLinkedChat(a)">Open chat</v-btn>
              <v-btn size="x-small" class="bg-dark text-light" @click="openLinkedLicenses(a)">Licenses</v-btn>
            </div>
          </div>
          <div class="linked-bar__why">A shared IP can also mean the same office, household or mobile network, so treat it as a lead, not proof.</div>
        </div>
      </div>
      <!-- this customer's proxies on a server in an outage ProxyMonitor confirmed (or just back) -->
      <div v-for="o in userOutages" :key="o.server" class="outage-strip w-full" :class="o.state === 'outage' ? 'is-out' : 'is-ok'">
        <template v-if="o.state === 'outage'">Their proxy on <b>{{ o.server }}</b> is in a confirmed outage since {{ hhmm(o.since) }} (ProxyMonitor).</template>
        <template v-else><b>{{ o.server }}</b> came back online at {{ hhmm(o.ended_at) }} after {{ o.minutes }} min.</template>
      </div>
      <div ref="chatContent" class="chatContent flex flex-col overflow-y-auto w-full mb-3" @scroll="handleScroll">
        <div
          v-for="message in messageList"
          class="message-row"
          :class="{ 'message-row--mine': isMine(message) }"
          :key="message.id"
        >
          <div
            class="message-bubble"
            :class="isMine(message) ? 'message-bubble--admin' : 'message-bubble--customer'"
          >
            <div class="message-meta">
              <span class="message-role">{{ roleLabel(message) }}</span>
              <span>{{ formatDateTime(message.timestamp) }}</span>
              <button class="msg-delete" title="Delete this message" @click="deleteMessage(message)"><v-icon icon="mdi-trash-can-outline" size="14"></v-icon></button>
            </div>
            <div v-if="message.image_url" class="chat-image-wrap">
              <a :href="message.image_url" class="chat-image" @click.prevent="lightbox = message.image_url"><img :src="message.image_url" alt="picture" loading="lazy" /></a>
              <button class="chat-image-delete" title="Delete this picture (the message stays)" @click="deleteImage(message)">
                <v-icon icon="mdi-trash-can-outline" size="16"></v-icon> Delete picture
              </button>
            </div>
            <div v-if="message.file_url" class="chat-image-wrap">
              <a :href="message.file_url" class="chat-file" :download="message.file_name || 'file'" title="Download"><v-icon icon="mdi-file-download-outline" size="18"></v-icon> {{ message.file_name || 'file' }}</a>
              <button class="chat-image-delete" title="Delete this file (the message stays)" @click="deleteImage(message)">
                <v-icon icon="mdi-trash-can-outline" size="16"></v-icon> Delete file
              </button>
            </div>
            <p v-if="message.text" class="break-all">{{ message.text }}</p>
          </div>
        </div>
      </div>
      <div v-if="pendingImage" class="w-2/3 mt-3 flex items-center gap-3 pending-image">
        <img v-if="pendingPreview" :src="pendingPreview" alt="picture to send" />
        <span v-else class="chat-file"><v-icon icon="mdi-file-outline" size="18"></v-icon> {{ pendingImage.name }} ({{ fileSize(pendingImage.size) }})</span>
        <span class="text-sm" style="color: var(--pp-mute, #6b7280)">{{ pendingPreview ? 'Picture' : 'File' }} ready · add a caption if you like, then Send</span>
        <button class="pp-btn" style="padding: 4px 10px;" @click="clearPending">Remove</button>
      </div>
      <div class="w-2/3 mt-4 flex flex-row items-center gap-x-3">
        <v-text-field ref="input" v-model="messageInput" class="w-full" variant="outlined" density="compact" placeholder="Type your message here" @keyup.enter="send" @paste="onPaste" 
        rounded="1" style=""></v-text-field>
        <input ref="fileInput" type="file" accept="image/png,image/jpeg,image/gif,image/webp,.txt,.ovpn,.conf,.log,.csv,.json,.pdf" class="hidden" @change="onPickImage" />
        <v-icon icon="mdi-paperclip" size="30px" :disabled="sendingImage" title="Send a picture or a file (txt, ovpn, conf, log, csv, json, pdf; 5 MB)" style="cursor: pointer;" @click="fileInput.click()"></v-icon>
        <v-icon icon="mdi-send" color="blue" size="35px" @click="send"></v-icon>
      </div>
    </div>
    <!-- picture viewer: opens in the page instead of a new tab; click anywhere or press Esc to close -->
    <Teleport to="body">
      <div v-if="lightbox" class="chat-lightbox" @click="lightbox = ''" @keydown.esc="lightbox = ''" tabindex="0" ref="lightboxEl">
        <img :src="lightbox" alt="picture" @click.stop />
        <button type="button" class="chat-lightbox-close" aria-label="Close">&times;</button>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, nextTick, watch, ref, onMounted, onBeforeUnmount } from 'vue';
import { storeToRefs } from "pinia";
import UserList from './UserList.vue';
import { useAccountStore, useAlertStore } from "@/stores";
import { useChatStore } from "@/stores/chat.store";
import { useRouter } from "vue-router";
import { fetchWrapper } from "@/helpers";
import { useRoute } from "vue-router";
import { pushState, enablePush, disablePush, sendTestPush } from "@/helpers/push";
import { hourLabel } from "@/helpers/support-hours";

const alertStore = useAlertStore();
const chatStore = useChatStore();
const { messages, users } = storeToRefs(chatStore);
const messageInput = ref('')
const accountStoroe = useAccountStore();
const {user} = storeToRefs(accountStoroe);
const selectedUser = ref(null);
const mobileView = ref('list');   // phone only: 'list' or 'chat' (desktop shows both)
const router = useRouter();
const route = useRoute();
// live support status (what the customer Support button shows)
const SUPPORT_MODES = [{ key: 'auto', label: 'Automatic' }, { key: 'online', label: 'Online' }, { key: 'offline', label: 'Offline' }];
const supportForm = ref({ mode: 'auto', from: 9, to: 22 });
const supportState = ref({ online: true });
const supportSaving = ref(false);
const applySupport = (s) => { if (!s || !s.mode) return; supportForm.value = { mode: s.mode, from: s.from, to: s.to }; supportState.value = s; };
const loadSupport = async () => { try { applySupport(await fetchWrapper.get(`${process.env.VUE_APP_VUE_API_URL}/chat/support-status`)); } catch (e) { /* shown with defaults */ } };
const setSupport = async (change) => {
  const next = { ...supportForm.value, ...change };
  if (next.mode === 'auto' && next.from === next.to) { alertStore.error('Opening and closing hours must be different'); return; }
  supportSaving.value = true;
  try { applySupport(await fetchWrapper.post(`${process.env.VUE_APP_VUE_API_URL}/chat/support-status`, next)); alertStore.success('Support status saved'); }
  catch (e) { alertStore.error(e?.message || e || 'Could not save the support status'); }
  supportSaving.value = false;
};
loadSupport();
const supportTimer = setInterval(loadSupport, 60000);
onBeforeUnmount(() => clearInterval(supportTimer));

// push notifications (per device)
const pushStatus = ref('unsupported');
const pushBusy = ref(false);
pushState().then((s) => { pushStatus.value = s; }).catch(() => { /* no push support in this browser */ });
const togglePush = async () => {
  pushBusy.value = true;
  try { pushStatus.value = pushStatus.value === 'on' ? await disablePush() : await enablePush(); alertStore.success(pushStatus.value === 'on' ? 'Notifications enabled on this device' : 'Notifications disabled'); }
  catch (e) { alertStore.error(e?.message || 'Could not change notifications'); pushStatus.value = await pushState().catch(() => 'off'); }
  pushBusy.value = false;
};
const testPush = async () => { pushBusy.value = true; try { const r = await sendTestPush(); alertStore.success("Test sent to " + r.sent + " device(s)"); } catch (e) { alertStore.error(e?.message || 'Test failed'); } pushBusy.value = false; };
// customer summary shown in the header (age, licences, top-ups)
const summary = ref(null);
const summaryLoading = ref(false);
const money = (v) => Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '-';
const accountAge = computed(() => {
  if (!summary.value?.created_at) return '';
  const days = Math.floor((Date.now() - new Date(summary.value.created_at).getTime()) / 86400000);
  if (days < 1) return 'less than a day old';
  if (days < 60) return days + ' day' + (days === 1 ? '' : 's') + ' old';
  const months = Math.floor(days / 30.44);
  if (months < 24) return months + ' months old';
  const years = Math.floor(months / 12), rem = months % 12;
  return years + ' year' + (years === 1 ? '' : 's') + (rem ? ' ' + rem + ' month' + (rem === 1 ? '' : 's') : '') + ' old';
});
const loadSummary = async (u) => {
  summary.value = null;
  const id = u?.user_id ?? u?.id;
  if (!id) return;
  summaryLoading.value = true;
  try { summary.value = await fetchWrapper.get(`${process.env.VUE_APP_VUE_API_URL}/admin/user/summary?user_id=${id}`); }
  catch (_) { summary.value = null; }
  summaryLoading.value = false;
};
// accounts that signed up from the same IP as the selected customer
const linked = ref(null);
const linkedOpen = ref(true);
const loadLinked = async (u) => {
  linked.value = null;
  const id = u?.user_id ?? u?.id;
  if (!id) return;
  try { const res = await fetchWrapper.get(`${process.env.VUE_APP_VUE_API_URL}/admin/user/linked?user_id=${id}`); if (String(selectedUser.value?.user_id ?? selectedUser.value?.id) === String(id)) linked.value = res; }
  catch (_) { linked.value = null; }
};
// outages on the selected customer's servers
const userOutages = ref([]);
const hhmm = (v) => (v ? new Date(v).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');
const loadUserOutages = async (u) => {
  userOutages.value = [];
  const id = u?.user_id ?? u?.id;
  if (!id) return;
  try {
    const r = await fetchWrapper.get(`${process.env.VUE_APP_VUE_API_URL}/outages/mine?user_id=${id}`);
    const seen = new Set();
    if (String(selectedUser.value?.user_id ?? selectedUser.value?.id) === String(id)) userOutages.value = Object.values(r.licenses || {}).filter((o) => !seen.has(o.server) && seen.add(o.server));
  } catch (_) { userOutages.value = []; }
};
const openLinkedChat = (a) => {
  const known = (users.value || []).find((x) => String(x.user_id ?? x.id) === String(a.user_id));
  selectUser(known || { id: a.user_id, user_id: a.user_id, name: a.name, email: a.email });
};
const openActivity = () => { const id = selectedUser.value?.user_id ?? selectedUser.value?.id; if (id) window.open(router.resolve({ path: "/admin/audit", query: { user_id: id } }).href, "_blank", "noopener") };   // Audit log, new tab
const openLinkedLicenses = (a) => window.open(router.resolve({ path: "/admin/license", query: { email: a.email } }).href, "_blank", "noopener");
// Licences page already reads ?email= and pre-fills its customer filter (and the quick-add dialogs)
const openLicenses = () => { if (selectedUser.value?.email) window.open(router.resolve({ path: "/admin/license", query: { email: selectedUser.value.email } }).href, "_blank", "noopener") };   // new tab: keep chatting here
const chatContent = ref(null);
const input = ref(null)
let chatPoller = null;
let userPoller = null;
const stateUserList = () => {
  const userlist = document.getElementById('userlist')
  if (userlist.classList.contains('userhidden')) {
    // Show the user list (left to right)
    userlist.classList.remove('userhidden');
    userlist.classList.add('uservisible');
  } else {
    // Hide the user list (right to left)
    userlist.classList.remove('uservisible');
    userlist.classList.add('userhidden');
  }
}
const messageList = computed(() => messages.value);
const sameUserId = (left, right) => left !== undefined && left !== null && right !== undefined && right !== null
  && String(left) === String(right);
const selectedCustomerId = () => selectedUser.value?.user_id ?? selectedUser.value?.id;
const isCustomerMessage = (message) => sameUserId(message.from, selectedCustomerId());
const isMine = (message) => !isCustomerMessage(message);
const roleLabel = (message) => {
  if (!isCustomerMessage(message)) {
    return sameUserId(message.from, user.value?.id) ? 'Admin (You)' : 'Admin Support';
  }
  const customerName = selectedUser.value?.name || selectedUser.value?.email;
  return customerName ? `Customer: ${customerName}` : 'Customer';
};

const refreshUsers = async () => {
  await chatStore.getUsers();
};

// pictures: paperclip or paste; sent to the selected customer
const fileInput = ref(null);
const sendingImage = ref(false);
// a pasted or picked picture waits here (with a preview) until Send is pressed
const pendingImage = ref(null);
const pendingPreview = ref("");
const lightbox = ref("");        // picture shown full-size in the page (empty = closed)
const lightboxEl = ref(null);
watch(lightbox, (v) => { if (v) nextTick(() => lightboxEl.value && lightboxEl.value.focus()); });
const CHAT_FILE_EXT = ['txt', 'ovpn', 'conf', 'log', 'csv', 'json', 'pdf'];
const isChatFile = (f) => CHAT_FILE_EXT.includes(String(f?.name || '').split('.').pop().toLowerCase());
const fileSize = (n) => n > 1024 * 1024 ? (n / 1024 / 1024).toFixed(1) + ' MB' : Math.max(1, Math.round(n / 1024)) + ' KB';
const setPending = (file) => {
  if (!file || !(file.type.startsWith("image/") || isChatFile(file))) { alertStore.error("Only pictures or txt / ovpn / conf / log / csv / json / pdf files can be sent"); return; }
  if (file.size > 5 * 1024 * 1024) { alertStore.error("Files must be 5 MB or smaller"); return; }
  clearPending(); pendingImage.value = file; pendingPreview.value = file.type.startsWith("image/") ? URL.createObjectURL(file) : "";
};
const clearPending = () => { if (pendingPreview.value) URL.revokeObjectURL(pendingPreview.value); pendingImage.value = null; pendingPreview.value = ""; };
const sendImage = async () => {
  if (!pendingImage.value || !selectedUser.value?.user_id) { alertStore.error("Select a customer first"); return; }
  sendingImage.value = true;
  const m = await chatStore.uploadImage(pendingImage.value, selectedUser.value.user_id, messageInput.value.trim());
  if (m) { messageInput.value = ""; clearPending(); await nextTick(); if (chatContent.value) chatContent.value.scrollTop = chatContent.value.scrollHeight; }
  sendingImage.value = false;
};
// admin: remove a whole message, or just the picture on one
const deleteMessage = async (message) => {
  if (!window.confirm("Delete this message for both sides?")) return;
  await chatStore.deleteMessage(message.id);
};
const deleteImage = async (message) => {
  if (!window.confirm("Delete this attachment? The message stays with a 'file removed' note.")) return;
  await chatStore.deleteImage(message.id);
};
const onPickImage = (e) => { const f = e.target.files?.[0]; e.target.value = ""; setPending(f); };
const onPaste = (e) => { const item = [...(e.clipboardData?.items || [])].find((i) => i.type.startsWith("image/")); if (item) { e.preventDefault(); setPending(item.getAsFile()); } };

const selectUser = async (selecteduser) => {
  selectedUser.value = selecteduser;
  mobileView.value = 'chat';   // phone: switch from the list to the conversation
  nextTick(handleResize);   // measured once the conversation column is shown
  loadSummary(selecteduser);
  loadLinked(selecteduser);
  loadUserOutages(selecteduser);
  await chatStore.getMessages(selecteduser.user_id)
  await chatStore.messageRead(selecteduser.user_id)
  await refreshUsers()
  nextTick(() => {
    if (chatContent.value) chatContent.value.scrollTop = chatContent.value.scrollHeight;
  })
};

const unRead = async (user) => {
  await chatStore.markUnread(user.user_id)
}


const send = async () => {
  if (pendingImage.value) { await sendImage(); return; }   // picture waiting: send it (text becomes the caption)
  if(messageInput.value.trim() === '' || !selectedUser.value?.user_id)
  {
    alertStore.error("Please select user or enter message");
    return;
  }
  try {
    await chatStore.sendMessage(messageInput.value, selectedUser.value.user_id)
  } catch (error) {
    alertStore.error(error)
    return;
  }
  messageInput.value = ''
  nextTick(() => {
    input.value.focus()
    if (chatContent.value) chatContent.value.scrollTop = chatContent.value.scrollHeight;
  })
}

// the chat column fills the screen from where it starts down to the bottom (minus a small gap), so the reply box is
// always on screen whatever sits above it (support status, notifications, the browser's own bars)
const chatColumn = ref(null);
const BOTTOM_GAP = window.innerWidth < 768 ? 80 : 24;   // phone: room for the bottom bar
const computedHeight = ref(window.innerHeight - 250);

const handleResize = () => {
  const el = chatColumn.value;
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY;
  computedHeight.value = Math.max(320, window.innerHeight - top - BOTTOM_GAP);
};
onMounted(async () => {
  await refreshUsers();
  // opened from a notification or a link: ?user_id=<customer id> preselects that conversation
  const wanted = Number(route.query.user_id);
  if (wanted) { const u = (users.value || []).find((x) => Number(x.user_id ?? x.id) === wanted); if (u) await selectUser(u); }
  chatPoller = setInterval(async () => {
    handleResize();
    if (selectedUser.value?.user_id) {
      await chatStore.refreshMessages(selectedUser.value.user_id);
    }
  }, 4000);
  userPoller = setInterval(async () => {
    await refreshUsers();
  }, 4000);
  window.addEventListener('resize', handleResize);
  await nextTick();
  handleResize();
});

onBeforeUnmount(() => {
  if (chatPoller) clearInterval(chatPoller);
  if (userPoller) clearInterval(userPoller);
  window.removeEventListener('resize', handleResize);
});
let loadingMoreMessages = false;

const handleScroll = () => {
  if (chatContent.value.scrollTop === 0 && !loadingMoreMessages) {
    const previous = chatContent.value.scrollHeight
    loadingMoreMessages = true;
    chatStore.loadMoreMessages(selectedUser.value?.user_id).then(() => {
      nextTick(() => {
        loadingMoreMessages = false;
        chatContent.value.scrollTop = chatContent.value.scrollHeight - previous;
      });
    });
  }
};

const sortedCustomers = computed(() => {
  return users.value.map(customer => {
    const rawTime = customer.lastMessageTime || customer.last_message_time || customer.updatedAt || customer.createdAt;
    const lastMessageTime = rawTime ? new Date(rawTime) : null;
    return { ...customer, lastMessageTime };
  }).sort((a, b) => {
    if (!a.lastMessageTime) return 1;
    if (!b.lastMessageTime) return -1;
    return b.lastMessageTime - a.lastMessageTime;
  });
});
const formatDateTime = (timestamp) => {
  try {
  const date = typeof timestamp?.toDate === 'function' ? timestamp.toDate() : new Date(timestamp);
  if(date && !Number.isNaN(date.getTime())){
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}.${month}.${day}  ${hours}:${minutes}`;
  } else return ''
  } catch(error) {
    return ''
  }
};

watch(messages, () => {
  nextTick(() => {
    if (!loadingMoreMessages) {
      // chatContent.value.scrollIntoView({behavior: 'smooth'})
      if (chatContent.value) chatContent.value.scrollTop = chatContent.value.scrollHeight;
      if (selectedUser.value?.user_id) chatStore.messageRead(selectedUser.value.user_id)
    }
  })
}, {deep: true})
</script>

<style scoped>
.admin-chat-container {
  display: flex;
  height: 100%;
}
.chatContent { flex: 0 1 auto; min-height: 0; }   /* the reply box follows the last message; it reaches the bottom only when the chat fills the column */
.message-row {
  display: flex;
  justify-content: flex-end;
  padding: 6px 14px;
}

.message-row--mine {
  justify-content: flex-start;
}

.message-bubble {
  border: 2px solid #dedede;
  background-color: #f8fafc;
  color: #111827;
  border-top-left-radius: 30px;
  border-top-right-radius: 30px;
  border-bottom-right-radius: 30px;
  padding: 10px;
  max-width: min(76%, 460px);
  min-width: 150px;
  width: fit-content;
}

.message-bubble--customer {
  border-color: #cbd5e1;
  background: #f8fafc;
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 30px;
}

.message-bubble--admin {
  border-color: #93c5fd;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 30px;
  background-color: #dbeafe;
}

.message-meta {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  color: #64748b;
  font-size: 12px;
  margin-bottom: 4px;
}

.message-role {
  color: #1d4ed8;
  font-weight: 700;
}

@media only screen and (max-width: 500px) {
  .listshow {
    display: block; 
  }
}
</style>
<style scoped>
.chat-image img { max-width: min(100%, 320px); max-height: 260px; border-radius: 10px; display: block; margin-bottom: 4px; cursor: zoom-in; }
.chat-lightbox { position: fixed; inset: 0; z-index: 3000; background: rgba(0, 0, 0, 0.82); display: flex; align-items: center; justify-content: center; cursor: zoom-out; outline: none; }
.chat-lightbox img { max-width: 94vw; max-height: 92vh; border-radius: 8px; box-shadow: 0 8px 40px rgba(0, 0, 0, 0.6); cursor: default; }
.chat-lightbox-close { position: absolute; top: 14px; right: 18px; border: 0; background: transparent; color: #fff; font-size: 40px; line-height: 1; cursor: pointer; }
.chat-image-wrap { margin-bottom: 6px; }
.chat-file { display: inline-flex; align-items: center; gap: 6px; padding: 6px 10px; border-radius: 8px; border: 1px solid rgba(128,128,128,0.35); font-size: 13px; text-decoration: none; color: inherit; margin-bottom: 4px; }
.chat-file:hover { border-color: rgba(59,130,246,0.7); }
.chat-image-delete { border: 0; background: transparent; color: #b91c1c; font-size: 12px; padding: 0; display: inline-flex; align-items: center; gap: 4px; cursor: pointer; }
.chat-image-delete:hover { text-decoration: underline; }
.msg-delete { border: 0; background: transparent; color: inherit; opacity: 0.45; padding: 0 0 0 6px; cursor: pointer; line-height: 1; }
.msg-delete:hover { opacity: 1; color: #b91c1c; }
</style>
<style scoped>
.pending-image img { width: 72px; height: 72px; object-fit: cover; border-radius: 8px; border: 1px solid rgba(128,128,128,0.35); }
</style>

<style scoped>
.support-bar { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 14px; padding: 10px 12px; border: 1px solid var(--ui-line); border-radius: 12px; background: var(--ui-card); color: var(--ui-ink); font-size: 13.5px; }
.support-bar__label { font-weight: 700; }
.support-bar__modes { display: inline-flex; overflow: hidden; border: 1px solid var(--ui-line-strong); border-radius: 999px; }
.support-bar__modes button { padding: 5px 14px; border: 0; background: transparent; color: var(--ui-dim); font-weight: 600; cursor: pointer; }
.support-bar__modes button + button { border-left: 1px solid var(--ui-line-strong); }
.support-bar__modes button.is-on { background: var(--ui-pri); color: #ffffff; }
.support-bar__modes button.is-on.is-online { background: #1f8f74; }
.support-bar__modes button.is-on.is-offline { background: #6b6990; }
.support-bar__modes button:disabled { cursor: wait; }
.support-bar__hours { display: inline-flex; align-items: center; gap: 6px; margin: 0; color: var(--ui-dim); }
.support-bar__hours select { padding: 3px 6px; border: 1px solid var(--ui-line-strong); border-radius: 6px; background: var(--ui-page); color: var(--ui-ink); }
.support-bar__note { color: var(--ui-dim); }
.support-bar__now { display: inline-flex; align-items: center; gap: 6px; margin-left: auto; font-weight: 700; }
.support-bar__now i { width: 9px; height: 9px; border-radius: 50%; }
.support-bar__now.is-online i { background: #22c38e; }
.support-bar__now.is-offline i { background: #9a98b3; }
</style>

<style scoped>
.linked-bar { margin: 0 0 8px; border: 1px solid var(--ui-warn); border-radius: 12px; background: var(--ui-warn-soft); color: var(--ui-warn-ink); overflow: hidden; }
.linked-bar__top { display: flex; align-items: center; gap: 8px; width: 100%; padding: 8px 12px; border: 0; background: transparent; color: inherit; font-size: 13.5px; text-align: left; cursor: pointer; }
.linked-bar__chev { margin-left: auto; transition: transform 150ms ease; }
.linked-bar.is-open .linked-bar__chev { transform: rotate(180deg); }
.linked-bar__body { display: grid; gap: 8px; padding: 0 12px 12px; font-size: 13px; }
.linked-bar__ip code { background: transparent; color: inherit; font-weight: 700; padding: 0; }
.linked-bar__why { font-size: 12px; opacity: 0.85; }
.linked-row { display: grid; grid-template-columns: minmax(0, 1.4fr) 100px 150px 130px auto; gap: 10px; align-items: center; padding: 8px 10px; border-radius: 10px; background: rgba(0, 0, 0, 0.16); color: var(--ui-ink); }
@media (max-width: 1100px) { .linked-row { grid-template-columns: 1fr 1fr; } }
.linked-row small { display: block; font-size: 11px; color: var(--ui-dim); }
.linked-row__who { min-width: 0; }
.linked-row__who b { display: block; }
.linked-row__who span { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 12px; color: var(--ui-dim); }
.linked-row__btns { display: flex; gap: 6px; }
.linked-tag { display: inline-block; padding: 0 7px; border-radius: 999px; font-size: 11px; font-weight: 700; }
.linked-tag.is-active { background: var(--ui-ok-soft); color: var(--ui-ok-ink); }
.linked-tag.is-none { background: var(--ui-card); color: var(--ui-dim); }
@media (prefers-reduced-motion: reduce) { .linked-bar__chev { transition: none; } }
</style>

<style scoped>
.outage-strip { margin: 0 0 8px; padding: 8px 12px; border-radius: 10px; font-size: 13.5px; }
.outage-strip.is-out { border: 1px solid #8a3a3f; background: #4a2a2f; color: #f6c9c9; }
.outage-strip.is-ok { border: 1px solid #2f6b5f; background: #21403d; color: #9fe1cb; }
</style>
